// DRV-5a (#2883) — the web side of GET /tenant-integrations/me?provider=gdrive.
//
// Shapes mirror the server's A-1 projection (apps/api/src/routes/driveOauth.ts,
// projectMetadataForRole): `status` goes to every member; `metadata` is null
// below admin rank. Nothing here is hard-coded Drive data — every field the UI
// shows comes from this response.

import { apiFetch } from './api';

export type DriveIntegrationStatus = 'pending' | 'authorized' | 'revoked' | 'admin_policy_enforced';

export interface DriveIntegrationMetadata {
  scope:             string | null;
  last_verified_at:  string | null;
  admin_policy_code: string | null;
  account: {
    permissionId: string;
    emailAddress: string | null;
    displayName:  string | null;
    capturedAt:   string | null;
  } | null;
  pendingAccount: {
    permissionId: string;
    emailAddress: string | null;
    expiresAt:    string;
  } | null;
  health: {
    lastOkAt:    string | null;
    lastErrorAt: string | null;
    lastError:   string | null;
  } | null;
  disconnectedAt: string | null;
  revokeAtGoogle: string | null;
}

export interface DriveIntegration {
  status:   DriveIntegrationStatus | null;
  metadata: DriveIntegrationMetadata | null;
}

export const DRIVE_STATUS_PATH = '/tenant-integrations/me?provider=gdrive';

/** Reads the tenant's Drive integration. Tolerates a malformed body (→ not connected). */
export function fetchDriveIntegration(): Promise<DriveIntegration> {
  return apiFetch<DriveIntegration | undefined>(DRIVE_STATUS_PATH).then((res) => ({
    status:   (res?.status ?? null) as DriveIntegrationStatus | null,
    metadata: res?.metadata ?? null,
  }));
}

/**
 * The display state of a Drive integration.
 * - connected:         authorized, account recorded
 * - connected_unknown: authorized, no account recorded (pre-DRV-1) or no
 *                      metadata (a non-admin caller)
 * - revoked:           the token was rejected or revoked (not a deliberate disconnect)
 * - admin_policy:      a Workspace admin blocked access
 * - not_connected:     never connected, pending, or deliberately disconnected
 */
export type DriveDisplayState = 'connected' | 'connected_unknown' | 'revoked' | 'admin_policy' | 'not_connected';

export function driveDisplayState(i: DriveIntegration | null): DriveDisplayState {
  switch (i?.status) {
    case 'authorized':
      return i.metadata?.account?.emailAddress ? 'connected' : 'connected_unknown';
    case 'revoked':
      // A disconnect (DELETE /tenant-integrations/gdrive) also leaves the row
      // 'revoked', stamped with disconnectedAt. That is "not connected", not a
      // revocation banner. Non-admins see no metadata, so they get the banner.
      return i.metadata?.disconnectedAt ? 'not_connected' : 'revoked';
    case 'admin_policy_enforced':
      return 'admin_policy';
    default:
      return 'not_connected';
  }
}

/** A stored ISO timestamp as a local date-time; '' for absent or unparseable. */
export function formatDriveDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  return Number.isNaN(t) ? '' : new Date(t).toLocaleString();
}
